<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ReportsAPI from 'dashboard/api/reports';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  // { from, to } em epoch segundos - vindos do ReportFilters do BotReports
  filters: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isCrmEnabled = computed(() =>
  isFeatureEnabledOnAccount.value(accountId.value, FEATURE_FLAGS.CRM)
);

const isLoading = ref(false);
const data = ref(null);

const tokensTotal = computed(() => {
  const e = data.value?.engagement || {};
  return (e.tokens_in || 0) + (e.tokens_out || 0);
});

const formatPct = v => `${(v ?? 0).toFixed(1)}%`;
const formatNumber = v => (v == null ? '0' : Number(v).toLocaleString('pt-BR'));
const formatCurrency = v =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(v || 0);

async function fetchSummary() {
  if (!isCrmEnabled.value) return;
  if (!props.filters?.from || !props.filters?.to) return;
  isLoading.value = true;
  try {
    const res = await ReportsAPI.getBotAiCrmSummary({
      from: props.filters.from,
      to: props.filters.to,
    });
    data.value = res?.data || null;
  } catch {
    data.value = null;
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => [props.filters?.from, props.filters?.to, isCrmEnabled.value],
  fetchSummary,
  { immediate: true }
);
</script>

<template>
  <!-- Toda a sub-seção só renderiza quando a feature está habilitada na conta.
       Mantém o BotReports limpo para contas que não usam CRM. -->
  <section v-if="isCrmEnabled" class="flex flex-col gap-4">
    <header class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <i class="i-lucide-target size-4 text-n-brand" />
        <h2 class="text-heading-3 text-n-slate-12 mb-0">
          {{ t('BOT_REPORTS.AI_CRM.TITLE') }}
        </h2>
      </div>
      <span
        v-if="isLoading"
        class="text-body-small text-n-slate-11 inline-flex items-center gap-2"
      >
        <Spinner size="sm" /> {{ t('BOT_REPORTS.AI_CRM.LOADING') }}
      </span>
    </header>

    <!-- KPIs principais - uma linha de cards finos -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('BOT_REPORTS.AI_CRM.LEADS_CREATED') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ formatNumber(data?.leads?.ai_created) }}
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{
            t('BOT_REPORTS.AI_CRM.LEADS_CREATED_HINT', {
              pct: formatPct(data?.leads?.ai_created_pct),
              total: formatNumber(data?.leads?.total),
            })
          }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('BOT_REPORTS.AI_CRM.QUALIFICATION_RATE') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ formatPct(data?.leads?.ai_qualification_rate) }}
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{
            t('BOT_REPORTS.AI_CRM.QUALIFICATION_HINT', {
              count: formatNumber(data?.leads?.ai_qualified),
            })
          }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('BOT_REPORTS.AI_CRM.ACCEPTANCE_RATE') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ formatPct(data?.leads?.acceptance_rate) }}
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{
            t('BOT_REPORTS.AI_CRM.AVG_CONFIDENCE', {
              pct: `${((data?.leads?.avg_confidence || 0) * 100).toFixed(0)}%`,
            })
          }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('BOT_REPORTS.AI_CRM.FOLLOW_UPS') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          {{ formatNumber(data?.follow_ups?.ai_applied) }}
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{
            t('BOT_REPORTS.AI_CRM.FOLLOW_UPS_HINT', {
              pct: formatPct(data?.follow_ups?.ai_applied_pct),
            })
          }}
        </p>
      </div>
    </div>

    <!-- Consumo de IA + Top intents/tools - duas colunas -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-4 flex flex-col gap-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0"
        >
          {{ t('BOT_REPORTS.AI_CRM.USAGE') }}
        </p>
        <div class="grid grid-cols-2 gap-3 text-body-main">
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.EXECUTIONS') }}
            </p>
            <p class="font-semibold text-n-slate-12 mb-0">
              {{ formatNumber(data?.engagement?.total_executions) }}
            </p>
          </div>
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.AVG_ITERATIONS') }}
            </p>
            <p class="font-semibold text-n-slate-12 mb-0">
              {{ (data?.engagement?.avg_iterations || 0).toFixed(2) }}
            </p>
          </div>
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.TOKENS') }}
            </p>
            <p class="font-semibold text-n-slate-12 mb-0">
              {{ formatNumber(tokensTotal) }}
              <span class="font-normal text-body-small text-n-slate-10">
                {{
                  t('BOT_REPORTS.AI_CRM.TOKENS_BREAKDOWN', {
                    in: formatNumber(data?.engagement?.tokens_in),
                    out: formatNumber(data?.engagement?.tokens_out),
                  })
                }}
              </span>
            </p>
          </div>
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.COST') }}
            </p>
            <p class="font-semibold text-n-slate-12 mb-0">
              {{ formatCurrency(data?.engagement?.cost_usd) }}
            </p>
          </div>
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.HANDOFFS') }}
            </p>
            <p class="font-semibold text-n-amber-11 mb-0">
              {{ formatNumber(data?.engagement?.handoffs) }}
            </p>
          </div>
          <div>
            <p class="text-n-slate-11 mb-0.5 text-body-small">
              {{ t('BOT_REPORTS.AI_CRM.ERRORS') }}
            </p>
            <p class="font-semibold text-n-ruby-11 mb-0">
              {{ formatNumber(data?.engagement?.errors) }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-4 flex flex-col gap-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0"
        >
          {{ t('BOT_REPORTS.AI_CRM.TOP_INTENTS') }}
        </p>
        <ol v-if="data?.top_intents?.length" class="flex flex-col">
          <li
            v-for="(item, idx) in data.top_intents"
            :key="`intent-${idx}`"
            class="flex items-center justify-between py-1.5 border-b border-n-weak last:border-b-0"
          >
            <span class="text-body-main text-n-slate-12 truncate">{{
              item.name
            }}</span>
            <span class="text-body-small font-semibold text-n-slate-11">{{
              formatNumber(item.count)
            }}</span>
          </li>
        </ol>
        <p v-else class="text-body-small text-n-slate-11 mb-0">
          {{ t('BOT_REPORTS.AI_CRM.EMPTY') }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-4 flex flex-col gap-3 lg:col-span-2"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0"
        >
          {{ t('BOT_REPORTS.AI_CRM.TOP_TOOLS') }}
        </p>
        <div v-if="data?.top_tools?.length" class="flex flex-wrap gap-2">
          <span
            v-for="(item, idx) in data.top_tools"
            :key="`tool-${idx}`"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-n-alpha-2 text-body-small text-n-slate-12"
          >
            <i class="i-lucide-wrench size-3 text-n-slate-11" />
            <span class="font-mono">{{ item.name }}</span>
            <span class="text-n-slate-11">·</span>
            <span class="font-semibold">{{ formatNumber(item.count) }}</span>
          </span>
        </div>
        <p v-else class="text-body-small text-n-slate-11 mb-0">
          {{ t('BOT_REPORTS.AI_CRM.EMPTY_TOOLS') }}
        </p>
      </div>
    </div>
  </section>
</template>
